import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import BackLink from "@/components/BackLink";
import PendingDraftBanner from "@/components/PendingDraftBanner";
import PostCreateGalleri from "@/components/PostCreateGalleri";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function modHeaders() {
  return { Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` };
}

export default function ModCreateForening() {
  const navigate = useNavigate();
  // Etappe 4.1 — defaulta andrad_av till inloggad användares namn
  const user = JSON.parse(sessionStorage.getItem("mod_user") || "null");
  const [form, setForm] = useState({
    namn: "", beskrivning: "", ort: "", kontaktperson: "",
    kontakt_epost: "", kontakt_telefon: "", webbplats: "", publish: true,
    andrad_av: (user?.name || user?.email || "").trim(),
    andringsnotering: "",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [savedId, setSavedId] = useState(null); // Build 96.21

  const set = (f, v) => { setForm(p => ({ ...p, [f]: v })); if (errors[f]) setErrors(p => ({ ...p, [f]: null })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.namn.trim()) errs.namn = "Ange namn";
    if (!form.beskrivning.trim()) errs.beskrivning = "Ange beskrivning";
    // Etappe 4.1 — Ändrad av Pflicht
    if (!form.andrad_av.trim()) errs.andrad_av = "Ange namn på den som ändrar posten innan du sparar.";
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/mod/foreningar/create`, {
        namn: form.namn, beskrivning: form.beskrivning,
        ort: form.ort || null, kontaktperson: form.kontaktperson || null,
        kontakt_epost: form.kontakt_epost || null, kontakt_telefon: form.kontakt_telefon || null,
        webbplats: form.webbplats || null, publish: form.publish,
        andrad_av: form.andrad_av.trim(),
        andringsnotering: form.andringsnotering || null,
      }, { headers: modHeaders() });
      setSavedId(res.data.id); // Build 96.21
    } catch (err) {
      if (err.response?.status === 401) navigate("/mod/login");
      else setErrors({ submit: err?.response?.data?.detail || "Något gick fel" });
    }
    setBusy(false);
  };

  if (savedId) {
    return (
      <div data-testid="mod-create-forening-success" className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <BackLink to="/mod/foreningar" label="Föreningar" />
        <PostCreateGalleri
          collection="foreningar"
          itemId={savedId}
          titel={form.namn}
          backTo="/mod/foreningar"
          backLabel="Tillbaka till föreningar"
          publikt={form.publish}
        />
      </div>
    );
  }

  return (
    <div data-testid="mod-create-forening-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <BackLink to="/mod/foreningar" label="Föreningar" />
      <PendingDraftBanner urlPattern="/api/mod/foreningar" />
      <h1 className="font-heading text-2xl font-medium text-ink mb-6">Lägg till förening</h1>
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <Field label="Namn" error={errors.namn} required>
          <input data-testid="forening-namn" value={form.namn} onChange={e => set("namn", e.target.value)} className="input-mod" />
        </Field>
        <Field label="Kort beskrivning" error={errors.beskrivning} required>
          <textarea data-testid="forening-beskrivning" value={form.beskrivning} onChange={e => set("beskrivning", e.target.value)} rows={3} className="input-mod" />
        </Field>
        <Field label="Ort / område">
          <input value={form.ort} onChange={e => set("ort", e.target.value)} placeholder="T.ex. Backe, Rossön" className="input-mod" />
        </Field>
        <Field label="Kontaktperson">
          <input value={form.kontaktperson} onChange={e => set("kontaktperson", e.target.value)} className="input-mod" />
        </Field>
        <Field label="Kontaktväg (valfritt)">
          <input value={form.kontakt_epost} onChange={e => set("kontakt_epost", e.target.value)} placeholder="E-post" className="input-mod mb-2" />
          <input value={form.kontakt_telefon} onChange={e => set("kontakt_telefon", e.target.value)} placeholder="Telefon" className="input-mod" />
        </Field>
        <Field label="Webbplats / social länk">
          <input value={form.webbplats} onChange={e => set("webbplats", e.target.value)} placeholder="https://..." className="input-mod" />
        </Field>
        {/* Etappe 4.1 — Pflicht-Audit-Felder */}
        <Field label="Ändrad av" error={errors.andrad_av} required>
          <input
            data-testid="forening-andrad-av"
            value={form.andrad_av}
            onChange={e => set("andrad_av", e.target.value)}
            placeholder="Ange namn på den som ändrar posten"
            className="input-mod"
          />
        </Field>
        <Field label="Ändringsnotering (valfritt)">
          <input
            value={form.andringsnotering}
            onChange={e => set("andringsnotering", e.target.value)}
            placeholder="Kort kommentar om vad som ändrades"
            className="input-mod"
          />
        </Field>
        <div className="flex items-center gap-3">
          <input type="checkbox" checked={form.publish} onChange={e => set("publish", e.target.checked)} className="w-5 h-5 accent-pine" />
          <label className="text-base text-ink">Publicera direkt</label>
        </div>
        {errors.submit && <p className="text-falu text-base">{errors.submit}</p>}
        <div className="flex gap-3 mt-2">
          <button type="submit" disabled={busy} data-testid="forening-submit" className="bg-pine hover:bg-pine-hover text-white font-medium px-6 py-3 rounded-md text-base transition-colors disabled:opacity-50">
            {busy ? "Sparar..." : form.publish ? "Skapa och publicera" : "Spara som utkast"}
          </button>
          <button type="button" onClick={() => navigate("/mod/foreningar")} className="text-muted-foreground hover:text-ink px-4 py-3 text-base">Avbryt</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, error, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink mb-1">{label}{required && <span className="text-falu ml-1">*</span>}</label>
      {children}
      {error && <p className="text-falu text-sm mt-1">{error}</p>}
    </div>
  );
}
